import React, { ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles, withStyles } from 'tss-react/mui'
import {
  ClickAwayListener,
  Unstable_Grid2 as Grid,
  Tooltip,
  TooltipClasses,
  Typography,
} from '@mui/material'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { Info } from '@mui/icons-material'
import HandleMarkup from '../HandleMarkup'
import { sendProductInfoInteractionEvent } from '@src/utils/analytics'

const useStyles = makeStyles()((theme) => ({
  infoIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
    position: 'relative',
    top: theme.spacing(0.5),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: theme.typography.fontWeightMedium,
    marginRight: theme.spacing(1),
  },
  attrValue: {
    fontWeight: theme.typography.fontWeightRegular,
  },
  closeButton: {
    display: 'none',
  },
}))

const HtmlTooltip = withStyles(Tooltip, (theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontSize: theme.typography.pxToRem(12),
    boxShadow: '0 6px 13px 0 rgba(0, 0, 0, 0.16)',
    padding: theme.spacing(4, 5, 4, 5),
    minWidth: 250,
    maxWidth: 500,
  },
  arrow: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(20),
  },
  tooltipPlacementTop: {
    top: theme.spacing(2),
  },
}))

interface AdditionalInfoTooltipProps {
  customClasses?: Partial<TooltipClasses>
  dataTestId?: string
  material?: ValidMaterialPricingDetailFragment | null
  showAdditionalColumns?: boolean
  showTitle?: boolean
  tooltipContent?: ReactNode
}

const AdditionalInfoColumn = ({ id, defaultMessage, columnValue }) => {
  const { classes } = useStyles()

  return (
    <Grid container>
      <Grid>
        <Typography variant="caption" className={classes.label}>
          <FormattedMessage id={id} defaultMessage={defaultMessage} />
        </Typography>
      </Grid>
      <Grid>
        <Typography variant="caption" className={classes.attrValue}>
          <HandleMarkup value={columnValue} />
        </Typography>
      </Grid>
    </Grid>
  )
}

const AdditionalInfoTooltip = ({
  customClasses,
  dataTestId,
  material,
  showAdditionalColumns = true,
  showTitle = true,
  tooltipContent,
}: AdditionalInfoTooltipProps) => {
  const { classes } = useStyles()
  const [open, setOpen] = useState(false)

  const additionalInfoFields = [
    {
      id: 'ADDITIONAL_INFO_CARRIER_RESTRICTION',
      defaultMessage: 'Additional Fees/Restrictions:',
      key: 'carrierRestriction',
    },
    {
      id: 'ADDITIONAL_INFO_FTCC',
      defaultMessage: 'Foreign Trade Commodity Code:',
      key: 'tariff',
    },
    {
      id: 'ADDITIONAL_INFO_UNTN',
      defaultMessage: 'UN Transportation Number(IATA):',
      key: 'unNumber',
    },
    {
      id: 'ADDITIONAL_INFO_CAS',
      defaultMessage: 'CAS Number:',
      key: 'casNumber',
    },
    {
      id: 'ADDITIONAL_INFO_JFC',
      defaultMessage: 'Japan Fire Code:',
      key: 'jfcCode',
    },
    {
      id: 'ADDITIONAL_INFO_PDSC',
      defaultMessage: 'Poison/Deleterious Substance Code:',
      key: 'pdcCode',
    },
  ]

  const handleOpen = () => {
    sendProductInfoInteractionEvent({
      action: 'view additional information',
      section: 'pricing box',
      component: 'right rail',
      elementType: 'icon',
      elementText: 'view additional information',
      linkUrl: undefined,
      productId: material?.product,
      productBrand: material?.brand,
      productVariant: material?.materialNumber,
    })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <span>
        <HtmlTooltip
          arrow
          classes={customClasses}
          data-testid={dataTestId}
          placement="top"
          open={open}
          onClick={handleOpen}
          title={
            <>
              {showTitle && (
                <Typography variant="h3" className={classes.title}>
                  <FormattedMessage
                    id="PRODUCT_INFORMATION"
                    defaultMessage="Product Information"
                  />
                </Typography>
              )}
              {showAdditionalColumns &&
                additionalInfoFields.map(
                  (field) =>
                    material?.additionalInfo?.[field.key] && (
                      <AdditionalInfoColumn
                        id={field.id}
                        key={field.key}
                        defaultMessage={field.defaultMessage}
                        columnValue={material.additionalInfo[field.key]}
                      />
                    )
                )}
              {material?.packageType && material?.packageSize && (
                <AdditionalInfoColumn
                  id="ADDITIONAL_INFO_PACKAGING_INFO"
                  defaultMessage="Packaging Information:"
                  columnValue={`${material.packageSize} in ${material.packageType}`}
                />
              )}
              {tooltipContent}
            </>
          }
        >
          <Info className={classes.infoIcon} />
        </HtmlTooltip>
      </span>
    </ClickAwayListener>
  )
}

export default AdditionalInfoTooltip
